import React, { useEffect, useState, useRef } from 'react';
import { X, ArrowUp, ArrowDown, Loader2, FolderTree, Check, AlertCircle } from 'lucide-react';
import { useMenuCategories } from '../../lib/menu-hooks';
import { cn } from '../../lib/utils';
import { MenuCategory } from '../../lib/types';
import { supabase } from '../../lib/supabase';
interface CategoryReorderDrawerProps {
  restaurantId: string;
  slug?: string;
  isOpen: boolean;
  onClose: () => void;
}
interface ParentGroup {
  key: string;
  label: string;
  categoryNames: string[];
}
const NATUCAFE_GROUPS: ParentGroup[] = [{
  key: 'bebidas',
  label: 'Bebidas y Postres',
  categoryNames: ['bebidas calientes', 'bebidas frías', 'té y más', 'aguadulce', 'batidos en agua', 'batidos con leche', 'batidos', 'postres']
}, {
  key: 'comida',
  label: 'Comida',
  categoryNames: []
}];
function getGroupsForSlug(slug?: string): ParentGroup[] | null {
  if (slug?.includes('natucafe')) return NATUCAFE_GROUPS;
  return null;
}
// Crack Pollo (restaurant_id 20): flat list of top-level parent categories,
// no group tabs, no parent/child filtering. The DB has 13 parents with no children.
const CRACK_POLLO_RESTAURANT_ID = '20';
export function CategoryReorderDrawer({
  restaurantId,
  slug,
  isOpen,
  onClose
}: CategoryReorderDrawerProps) {
  const {
    categories,
    loading: catLoading,
    refetch: refetchCategories
  } = useMenuCategories(restaurantId);
  const [selectedGroupKey, setSelectedGroupKey] = useState<string>('');
  const [localCategories, setLocalCategories] = useState<MenuCategory[]>([]);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const isCrackPollo = String(restaurantId) === CRACK_POLLO_RESTAURANT_ID;
  const hardcodedGroups = isCrackPollo ? null : getGroupsForSlug(slug);
  const [dbGroups, setDbGroups] = useState<{
    key: string;
    label: string;
    childIds: Set<string>;
  }[]>([]);
  const lastSyncedGroupRef = useRef<string | null>(null);
  useEffect(() => {
    if (categories.length === 0) return;
    if (isCrackPollo) {
      if (!selectedGroupKey) setSelectedGroupKey('__crackpollo_parents__');
      return;
    }
    if (hardcodedGroups) {
      if (!selectedGroupKey) setSelectedGroupKey(hardcodedGroups[0].key);
      return;
    }
    const referencedParentIds = new Set(categories.filter(c => c.parent_category_id && String(c.parent_category_id) !== String(c.id)).map(c => String(c.parent_category_id)));
    const selfRefIds = new Set(categories.filter(c => c.parent_category_id && String(c.parent_category_id) === String(c.id)).map(c => String(c.id)));
    const parents = categories.filter(c => c.is_parent === true || referencedParentIds.has(String(c.id)) || selfRefIds.has(String(c.id)));
    if (parents.length > 0) {
      const built = parents.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)).map(parent => ({
        key: String(parent.id),
        label: parent.name ?? 'Sin nombre',
        childIds: new Set(categories.filter(c => String(c.parent_category_id) === String(parent.id) && String(c.id) !== String(parent.id)).map(c => String(c.id)))
      })).filter(g => g.childIds.size > 0);
      setDbGroups(built);
      if (!selectedGroupKey && built.length > 0) setSelectedGroupKey(built[0].key);
    } else {
      setDbGroups([{
        key: '__all__',
        label: 'Todas',
        childIds: new Set(categories.map(c => String(c.id)))
      }]);
      if (!selectedGroupKey) setSelectedGroupKey('__all__');
    }
  }, [categories, hardcodedGroups, selectedGroupKey, isCrackPollo]);
  // Resync local order from server only when the group changes, or when the
  // SET of category IDs in this group actually changes. While there are
  // unsaved local changes, never overwrite the user's draft.
  useEffect(() => {
    if (!selectedGroupKey || categories.length === 0) {
      setLocalCategories([]);
      lastSyncedGroupRef.current = null;
      return;
    }
    if (isDirty) return;
    let filtered: MenuCategory[];
    if (isCrackPollo) {
      filtered = categories.filter(c => c.is_parent === true && !c.parent_category_id);
      const next = filtered.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
      reconcileLocal(next);
      return;
    }
    if (hardcodedGroups) {
      const group = hardcodedGroups.find(g => g.key === selectedGroupKey);
      if (!group) {
        setLocalCategories([]);
        return;
      }
      if (group.categoryNames.length === 0) {
        const otherNames = hardcodedGroups.filter(g => g.categoryNames.length > 0).flatMap(g => g.categoryNames);
        filtered = categories.filter(cat => {
          const n = (cat.name ?? '').toLowerCase().trim();
          return !otherNames.some(on => n.includes(on) || on.includes(n));
        });
      } else {
        filtered = categories.filter(cat => {
          const n = (cat.name ?? '').toLowerCase().trim();
          return group.categoryNames.some(gn => n.includes(gn) || gn.includes(n));
        });
      }
    } else {
      const dbGroup = dbGroups.find(g => g.key === selectedGroupKey);
      if (!dbGroup) {
        setLocalCategories([]);
        return;
      }
      filtered = categories.filter(c => dbGroup.childIds.has(String(c.id)));
    }
    filtered = filtered.filter(c => !c.is_parent);
    const next = filtered.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    reconcileLocal(next);
    function reconcileLocal(next: MenuCategory[]) {
      const groupChanged = lastSyncedGroupRef.current !== selectedGroupKey;
      setLocalCategories(prev => {
        if (groupChanged) {
          lastSyncedGroupRef.current = selectedGroupKey;
          return next;
        }
        const prevIds = prev.map(c => c.id);
        const nextIds = next.map(c => c.id);
        const sameSet = prevIds.length === nextIds.length && prevIds.every(id => nextIds.includes(id));
        if (sameSet && prev.length > 0) {
          const byId = new Map(next.map(c => [c.id, c]));
          return prev.map(p => byId.get(p.id) || p);
        }
        return next;
      });
    }
  }, [selectedGroupKey, categories, hardcodedGroups, dbGroups, isCrackPollo, isDirty]);
  useEffect(() => {
    if (!isOpen) {
      setJustSaved(false);
      setSaveError(null);
      setEditingId(null);
    }
  }, [isOpen]);
  if (!isOpen) return null;
  const groupTabs = isCrackPollo ? [] : hardcodedGroups ? hardcodedGroups.map(g => ({
    key: g.key,
    label: g.label
  })) : dbGroups.map(g => ({
    key: g.key,
    label: g.label
  }));
  // ── Local-only reordering helpers ────────────────────────────────────
  const handleMove = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0 || direction === 'down' && index === localCategories.length - 1) return;
    const newCats = [...localCategories];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    const temp = newCats[index];
    newCats[index] = newCats[swapIndex];
    newCats[swapIndex] = temp;
    setLocalCategories(newCats.map((cat, i) => ({
      ...cat,
      sort_order: i + 1
    })));
    setIsDirty(true);
    setJustSaved(false);
    setSaveError(null);
  };
  const handlePositionSubmit = (catId: string) => {
    const newPos = parseInt(editValue, 10);
    if (isNaN(newPos) || newPos < 1 || newPos > localCategories.length) {
      setEditingId(null);
      return;
    }
    const currentIndex = localCategories.findIndex(c => c.id === catId);
    if (currentIndex === -1 || currentIndex === newPos - 1) {
      setEditingId(null);
      return;
    }
    const newCats = [...localCategories];
    const [moved] = newCats.splice(currentIndex, 1);
    newCats.splice(newPos - 1, 0, moved);
    setLocalCategories(newCats.map((cat, i) => ({
      ...cat,
      sort_order: i + 1
    })));
    setEditingId(null);
    setIsDirty(true);
    setJustSaved(false);
    setSaveError(null);
  };
  const handleSave = async () => {
    if (saving || !isDirty || localCategories.length === 0) return;
    setSaving(true);
    setSaveError(null);
    setJustSaved(false);
    try {
      for (let i = 0; i < localCategories.length; i++) {
        const cat = localCategories[i];
        const {
          error
        } = await supabase.from('menu_categories').update({
          sort_order: i + 1
        }).eq('id', cat.id);
        if (error) throw error;
      }
      await refetchCategories();
      setIsDirty(false);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2500);
    } catch (err: any) {
      console.error('[NOIZA][category-reorder] save failed:', err);
      setSaveError(err?.message || 'No se pudo guardar el orden.');
    } finally {
      setSaving(false);
    }
  };
  const handleRequestClose = () => {
    if (isDirty) {
      const ok = window.confirm('Tenés cambios sin guardar. ¿Querés descartarlos?');
      if (!ok) return;
      setIsDirty(false);
      lastSyncedGroupRef.current = null;
    }
    onClose();
  };
  return <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] transition-opacity" onClick={handleRequestClose} data-id="element-515" />

      <div className="fixed inset-y-0 right-0 w-full sm:w-[440px] bg-[#141414] border-l border-white/10 shadow-2xl z-[101] flex flex-col transform transition-transform duration-300" data-id="element-516">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10 shrink-0" data-id="element-517">
          <div className="flex items-center gap-3" data-id="element-518">
            <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center" data-id="element-519">
              <FolderTree className="w-5 h-5 text-white/70" data-id="element-520" />
            </div>
            <div data-id="element-521">
              <h2 className="text-lg font-bold text-white leading-tight" data-id="element-522">
                Ordenar Categorías
              </h2>
              <p className="text-xs text-white/40" data-id="element-523">
                Acomodá el orden y luego presioná{' '}
                <span className="text-white/70 font-semibold" data-id="element-524">
                  Guardar cambios
                </span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2" data-id="element-525">
            {isDirty && !saving && <span className="hidden sm:inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300 text-[10px] font-bold uppercase tracking-wider" data-id="element-526">
                Sin guardar
              </span>}
            {justSaved && !isDirty && <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[10px] font-bold uppercase tracking-wider" data-id="element-527">
                <Check className="w-3 h-3" data-id="element-528" /> Guardado
              </span>}
            <button onClick={handleRequestClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 transition-colors" data-id="element-529">
              <X className="w-5 h-5" data-id="element-530" />
            </button>
          </div>
        </div>

        {/* Group Tabs */}
        {groupTabs.length > 1 && <div className="p-4 border-b border-white/10 shrink-0" data-id="element-531">
            <div className="flex gap-2" data-id="element-532">
              {groupTabs.map(tab => {
            const isActive = selectedGroupKey === tab.key;
            return <button key={tab.key} onClick={() => {
              if (isActive) return;
              if (isDirty) {
                const ok = window.confirm('Tenés cambios sin guardar. ¿Descartarlos?');
                if (!ok) return;
                setIsDirty(false);
              }
              setSelectedGroupKey(tab.key);
              setEditingId(null);
              setJustSaved(false);
              setSaveError(null);
            }} className={cn('flex-1 px-4 py-2.5 rounded-xl text-sm font-bold transition-colors', isActive ? 'bg-white text-black' : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white')} data-id="element-533">
                    {tab.label}
                  </button>;
          })}
            </div>
          </div>}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4" data-id="element-534">
          {catLoading && localCategories.length === 0 ? <div className="flex items-center justify-center py-12" data-id="element-535">
              <Loader2 className="w-8 h-8 text-white/20 animate-spin" data-id="element-536" />
            </div> : localCategories.length === 0 ? <div className="text-center text-white/40 py-8" data-id="element-537">
              No hay categorías en esta sección
            </div> : <div className="flex flex-col gap-2" data-id="element-538">
              {localCategories.map((cat, index) => <div key={cat.id} className="flex items-center gap-3 p-3 rounded-xl border bg-white/5 border-white/10 hover:border-white/20 transition-colors" data-id="element-539">
                  <div className="flex flex-col gap-1 shrink-0" data-id="element-540">
                    <button onClick={() => handleMove(index, 'up')} disabled={index === 0 || saving} className="p-1 rounded-lg text-white/40 hover:text-white hover:bg-white/10 disabled:opacity-30 transition-colors" data-id="element-541">
                      <ArrowUp className="w-3.5 h-3.5" data-id="element-542" />
                    </button>
                    <button onClick={() => handleMove(index, 'down')} disabled={index === localCategories.length - 1 || saving} className="p-1 rounded-lg text-white/40 hover:text-white hover:bg-white/10 disabled:opacity-30 transition-colors" data-id="element-543">
                      <ArrowDown className="w-3.5 h-3.5" data-id="element-544" />
                    </button>
                  </div>

                  {editingId === cat.id ? <form onSubmit={e => {
              e.preventDefault();
              handlePositionSubmit(cat.id);
            }} className="shrink-0" data-id="element-545">
                      <input type="number" min={1} max={localCategories.length} value={editValue} onChange={e => setEditValue(e.target.value)} onBlur={() => handlePositionSubmit(cat.id)} autoFocus className="w-10 h-8 rounded-lg bg-white text-black text-center text-sm font-bold outline-none" data-id="element-546" />
                    </form> : <button onClick={() => {
              setEditingId(cat.id);
              setEditValue(String(index + 1));
            }} className="w-10 h-8 rounded-lg bg-white/10 flex items-center justify-center shrink-0 hover:bg-white/20 transition-colors cursor-text" title="Clic para escribir posición" data-id="element-547">
                      <span className="text-sm font-bold text-white/60" data-id="element-548">
                        {index + 1}
                      </span>
                    </button>}

                  <div className="flex-1 min-w-0" data-id="element-549">
                    <div className="font-bold text-white truncate" data-id="element-550">
                      {cat.name}
                    </div>
                  </div>
                </div>)}
            </div>}
        </div>

        {/* Sticky Save Footer */}
        <div className="border-t border-white/10 bg-[#0F0F0F] p-4 shrink-0 space-y-2" data-id="element-551">
          {saveError && <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-xs" data-id="element-552">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" data-id="element-553" />
              <span data-id="element-554">{saveError}</span>
            </div>}
          <button onClick={handleSave} disabled={!isDirty || saving || localCategories.length === 0} className={cn('w-full h-12 rounded-xl text-sm font-black flex items-center justify-center gap-2 transition-all', isDirty && !saving ? 'bg-amber-500 hover:bg-amber-400 text-black active:scale-95 shadow-lg' : 'bg-white/5 text-white/30 cursor-not-allowed')} data-id="element-555">
            {saving ? <>
                <Loader2 className="w-4 h-4 animate-spin" data-id="element-556" />
                Guardando...
              </> : justSaved && !isDirty ? <>
                <Check className="w-4 h-4" data-id="element-557" />
                Guardado
              </> : isDirty ? '💾 Guardar cambios' : 'Sin cambios para guardar'}
          </button>
          <p className="text-[10px] text-white/30 text-center leading-snug" data-id="element-558">
            Los cambios solo se verán en el menú del cliente después de
            guardarlos.
          </p>
        </div>
      </div>
    </>;
}